import { Link } from 'react-router-dom';

export function IntroToIgnusPage() {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <Link
          to="/ignus"
          className="text-text-muted hover:text-accent-gold transition-colors inline-flex items-center gap-1"
        >
          <BackIcon className="w-4 h-4" />
          Back to Ignus
        </Link>
      </div>

      <h1 className="font-display text-3xl font-bold text-text-primary mb-6">
        Introduction to Ignus
      </h1>

      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="card p-6 border-orange-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <FlameIcon className="w-5 h-5 text-orange-500" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              Welcome to Ignus
            </h2>
          </div>
          <div className="space-y-4 text-text-secondary">
            <p>
              Welcome to the world of Ignus, hero. At first glance, Ignus doesn't seem all that
              different from the Earth that you know. The continents are in different places, of
              course, but it's a world of mountains, plains, oceans, rivers, lakes, and deserts
              just like Earth.
            </p>
            <p>
              Look closer, however, and you will see that the denizens of Ignus are very different.
              Multiple species of humanoid fill its cities and towns and its wildernesses are
              populated by all manner of strange and wondrous creatures.
            </p>
            <p className="text-text-muted italic">
              As we step into our adventure, here's what you need to know about Ignus...
            </p>
          </div>
        </div>

        {/* A Child's History */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-accent-gold/10 flex items-center justify-center">
              <BookIcon className="w-5 h-5 text-accent-gold" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              Ignus: A Child's History
            </h2>
          </div>
          <div className="space-y-4 text-text-secondary">
            <p>
              Many hundreds of years ago, in{' '}
              <span className="text-text-primary font-medium">the time before</span>, the world of
              Ignus was a place of magic and wonder. Vast, continent-spanning civilizations created
              amazing works of arcane might that are far beyond our modern understanding.
            </p>
            <p>
              But great power corrupts and the people turned their arcane abilities against each
              other. The three great civilizations of Ignus—
              <span className="text-accent-gold">The Raathan Archive</span>,{' '}
              <span className="text-accent-gold">The Shaungin Empire</span>, and{' '}
              <span className="text-accent-gold">The People of Kaltash</span>—went to war against
              each other, creating weapons of mass destruction and causing untold devastation and
              loss of life.
            </p>
            <p>
              We call this time{' '}
              <span className="text-red-400 font-medium">"The Destruction"</span> and it nearly
              wiped out civilization as we know it.
            </p>
          </div>
        </div>

        {/* The Saviors */}
        <div className="card p-6">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            The Saviors of Mortalkind
          </h2>
          <div className="space-y-4 text-text-secondary">
            <p>Thankfully, mortalkind had great powers to protect them from themselves.</p>

            <div className="bg-background-elevated rounded-lg p-4">
              <h3 className="font-semibold text-accent-gold mb-2">The Titans</h3>
              <p className="text-sm">
                Despite the protests of some of their peers (now known as the Shadow Assembly, or
                simply "the Shadowed"), the Titans of{' '}
                <span className="text-accent-gold">The Crystal Assembly</span> took pity on mortals
                and entered the fight against the great arcane powers. By lending their magical
                energies to their followers and sometimes even directly intervening at great risk to
                their own existence, they helped smash the enemies of mortalkind and began the
                process of healing those broken by the conflict.
              </p>
            </div>

            <div className="bg-background-elevated rounded-lg p-4">
              <h3 className="font-semibold text-green-400 mb-2">Ignus Itself</h3>
              <p className="text-sm">
                At the same time, the world of Ignus itself seemingly entered the fight. Vast
                primordial energies surged from the planet, empowering its own protectors with a
                different kind of magic. The druid circles arose and, using the power of Ignus
                itself, tore apart the unnatural devices and creations of the arcane powers and
                began the long process of healing our planet.
              </p>
            </div>
          </div>
        </div>

        {/* The Aftermath */}
        <div className="card p-6">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            The Aftermath
          </h2>
          <div className="space-y-4 text-text-secondary">
            <p>
              In the aftermath of The Destruction, arcane powers were outlawed and those who
              practiced arcane magic were hunted down and... dealt with.
            </p>
            <p>
              In place of the great arcanocracies of the time before, two new ruling powers arose:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-background-elevated rounded-lg p-4">
                <h3 className="font-semibold text-accent-gold mb-2">The Seat</h3>
                <p className="text-sm">
                  Made up of the devout faithful who are allied with and serve the pantheon of
                  titans known as The Crystal Assembly, The Seat is a network of churches, temples,
                  and other communities of devotion. They are led by a head priest known as{' '}
                  <span className="text-text-primary">The High Seat</span> and the{' '}
                  <span className="text-text-primary">Council of Primarchs</span>.
                </p>
              </div>

              <div className="bg-background-elevated rounded-lg p-4">
                <h3 className="font-semibold text-green-400 mb-2">The Circle</h3>
                <p className="text-sm">
                  Made up of those who see themselves as protectors of Ignus, the Circle is the
                  ruling council of druids. They believe that Ignus is actually a sleeping
                  super-titan or maybe even a true deity and that all living beings (including the
                  titans) are a creation of the magical energy emitted by Ignus in its slumber. In
                  their minds, we are all, in effect, products of Ignus's dreams.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Ignus */}
        <div className="card p-6 border-green-500/20">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            Modern Ignus
          </h2>
          <div className="space-y-4 text-text-secondary">
            <p>
              Now, Ignus exists in a state of peaceful balance. The Seat and The Circle work
              together to ensure mortalkind's continued existence and to protect us from the dangers
              of arcane magic and its practitioners.
            </p>
            <p>
              Society is rigidly structured. Everyone knows their place and, if they don't, they're
              often told what their place is. There are four major factions on Ignus:{' '}
              <span className="text-green-400">The Circle</span>,{' '}
              <span className="text-accent-gold">The Seat</span>,{' '}
              <span className="text-blue-400">Civil Society</span>, and{' '}
              <span className="text-red-400">The Shunned</span>.
            </p>
          </div>

          <div className="mt-4 pt-4 border-t border-border-default">
            <Link
              to="/ignus/factions"
              className="text-accent-gold hover:underline inline-flex items-center gap-1"
            >
              Learn more about the Factions
              <ArrowIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Quick Links */}
        <div className="card p-6 bg-background-elevated">
          <h2 className="font-display text-lg font-semibold text-text-primary mb-4">
            Continue Exploring
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/ignus/geography"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <MapIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Geography & Regions</span>
            </Link>
            <Link
              to="/ignus/factions"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <FactionIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">The Four Factions</span>
            </Link>
            <Link
              to="/ignus/magic"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <MagicIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Magic & Titans</span>
            </Link>
            <Link
              to="/ignus/races"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <SpeciesIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Species & Lineages</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function MapIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  );
}

function FactionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function MagicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function SpeciesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
